import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  AlertCircle,
  AlertTriangle,
  Download,
  FileText,
  Loader2,
  Lock,
  PenLine,
  Search,
  ShieldCheck,
  Timer,
  UserX,
} from 'lucide-react';
import {
  anonymiser,
  apercuAnonymisation,
  chercherPersonnes,
  fetchClause,
  fetchDureesConservation,
  fetchRegistreTraitements,
  lienRegistre,
  reglerDureesConservation,
  signerClause,
  type PersonneTrouvee,
} from '../services/api';
import { Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';

import { dateFr } from '../utils/dates';
/**
 * Le droit à l'effacement, et les durées de conservation.
 *
 * Deux obligations se tiennent ici, et elles tirent en sens contraire.
 * Quelqu'un peut demander l'effacement de ses données, et l'association doit
 * l'accorder ; mais elle doit aussi pouvoir justifier ses comptes, et une
 * cotisation encaissée en 2024 figure à un bilan présenté en assemblée
 * générale.
 *
 * L'anonymisation tranche le nœud : la personne disparaît, le montant reste.
 * L'écran le dit avant chaque geste, parce que c'est exactement ce qu'on
 * craint en cliquant.
 */
export default function DonneesPersonnellesPage() {
  const { user, isLoading } = useAuth();
  const queryClient = useQueryClient();
  const estAdmin = user?.roles?.includes('ROLE_ADMIN') === true;

  const [notice, setNotice] = useState<{ type: 'ok' | 'erreur'; texte: string } | null>(null);
  const [recherche, setRecherche] = useState('');
  const [cible, setCible] = useState<PersonneTrouvee | null>(null);
  const [motDePasse, setMotDePasse] = useState('');
  const [motif, setMotif] = useState('');

  // Le registre est charge a part : il lit la configuration, pas les durees,
  // et un echec de l'un ne doit pas priver de l'autre.
  const registre = useQuery({
    queryKey: ['registre-traitements'],
    queryFn: fetchRegistreTraitements,
    enabled: estAdmin,
  });

  const durees = useQuery({
    queryKey: ['durees-conservation'],
    queryFn: fetchDureesConservation,
    enabled: estAdmin,
  });

  // La clause de sous-traitance et l'etat de ses deux signatures.
  const clause = useQuery({
    queryKey: ['clause-soustraitance'],
    queryFn: fetchClause,
    enabled: estAdmin,
  });

  const [signature, setSignature] = useState<{
    nom: string;
    qualite: string;
    lu: boolean;
    motDePasse: string;
  } | null>(null);

  const signer = useMutation({
    mutationFn: () => signerClause(signature!),
    onSuccess: (r) => {
      setNotice({ type: 'ok', texte: r.message ?? 'Clause signée.' });
      setSignature(null);
      queryClient.invalidateQueries({ queryKey: ['clause-soustraitance'] });
    },
    onError: (e: Error) => setNotice({ type: 'erreur', texte: e.message }),
  });

  const [brouillon, setBrouillon] = useState<Record<string, number>>({});

  const enregistrer = useMutation({
    mutationFn: () => reglerDureesConservation(brouillon),
    onSuccess: (r: any) => {
      setNotice({ type: 'ok', texte: r.message });
      setBrouillon({});
      queryClient.invalidateQueries({ queryKey: ['durees-conservation'] });
    },
    onError: (e: Error) => setNotice({ type: 'erreur', texte: e.message }),
  });

  const resultats = useQuery({
    queryKey: ['recherche-personnes', recherche],
    queryFn: () => chercherPersonnes(recherche),
    enabled: estAdmin && recherche.trim().length >= 2,
  });

  const apercu = useQuery({
    queryKey: ['apercu-anonymisation', cible?.type, cible?.id],
    queryFn: () => apercuAnonymisation(cible!.type, cible!.id),
    enabled: cible !== null,
  });

  const effacer = useMutation({
    mutationFn: () => anonymiser(cible!.type, cible!.id, motDePasse, motif),
    onSuccess: (r: any) => {
      setNotice({ type: 'ok', texte: r.message });
      setCible(null);
      setMotDePasse('');
      setMotif('');
      queryClient.invalidateQueries({ queryKey: ['recherche-personnes'] });
    },
    onError: (e: Error) => setNotice({ type: 'erreur', texte: e.message }),
  });

  if (isLoading) {
    return (
      <div className="p-8 flex justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-primary-600" />
      </div>
    );
  }

  if (!estAdmin) {
    return (
      <div className="p-4 lg:p-0 pb-24 lg:pb-0">
        <div className="card bg-red-50 border-red-200 text-red-800 flex items-start gap-3">
          <AlertCircle className="w-5 h-5 mt-0.5 shrink-0" />
          <div>
            <p className="font-semibold">Accès réservé</p>
            <p className="text-sm mt-1">
              L'effacement des données d'une personne est réservé aux administrateurs du site.
            </p>
          </div>
        </div>
      </div>
    );
  }

  const valeur = (cle: string, actuelle: number) =>
    brouillon[cle] !== undefined ? brouillon[cle] : actuelle;

  return (
    <div className="p-4 lg:p-0 space-y-5 pb-24 lg:pb-0">
      <div className="flex items-center gap-3">
        <ShieldCheck className="w-7 h-7 text-primary-700 shrink-0" />
        <div>
          <h1 className="text-2xl lg:text-3xl font-bold text-gray-900 dark:text-gray-100">
            Données personnelles
          </h1>
          <p className="text-sm lg:text-base text-gray-600 dark:text-gray-400">
            Combien de temps l'association garde, et comment elle efface quelqu'un qui le demande.
          </p>
        </div>
      </div>

      {notice && (
        <div
          className={`card text-sm flex items-start gap-3 ${
            notice.type === 'ok'
              ? 'bg-green-50 border-green-200 text-green-800'
              : 'bg-red-50 border-red-200 text-red-800'
          }`}
        >
          <AlertCircle className="w-5 h-5 mt-0.5 shrink-0" />
          <p className="flex-1">{notice.texte}</p>
          <button onClick={() => setNotice(null)} className="text-xs underline">Fermer</button>
        </div>
      )}

      {/* --------------------------- Le registre --------------------------- */}
      <section className="card space-y-4">
        <div className="flex items-center gap-2">
          <FileText className="w-5 h-5 text-primary-600 shrink-0" />
          <h2 className="font-semibold text-gray-900 dark:text-gray-100">
            Votre registre des traitements
          </h2>
        </div>

        <p className="text-sm text-gray-600 dark:text-gray-400">
          L'article 30 demande à l'association de recenser ce qu'elle fait des données des
          personnes. Le document est établi à partir de ce que le logiciel traite réellement —
          chaque durée citée est celle que vous avez réglée ci-dessous, chaque catégorie de
          données correspond à un champ existant — et rempli avec l'identité de votre association
          telle qu'elle figure dans vos paramètres.
        </p>

        {/* Trois morceaux du document ne se devinent pas depuis son titre, et
            ce sont les trois qu'une association cherche ailleurs faute de
            savoir qu'elle les a déjà. */}
        <p className="text-sm text-gray-600 dark:text-gray-400">
          Il contient aussi trois textes prêts à recopier, que le logiciel ne peut pas appliquer à
          votre place : la <strong>mention d'information pour votre bulletin d'adhésion</strong>,
          déjà remplie de votre identité et de vos durées, avec la case de consentement distincte
          qu'exige le certificat médical ; la <strong>procédure à suivre en cas de violation de
          données</strong>, en cinq étapes avec leurs délais — les 72 heures de l'article 33 ne se
          négocient pas, et un samedi soir n'est pas le moment d'apprendre la marche à suivre ; et
          la <strong>clause de sous-traitance à porter à votre contrat avec l'éditeur</strong>, qui
          l'engage à vous signaler tout incident sous 48 heures.
        </p>

        {registre.isPending ? (
          <Loader2 className="w-5 h-5 animate-spin text-primary-600" />
        ) : (
          <>
            <dl className="text-sm divide-y divide-gray-100 dark:divide-gray-800">
              {(registre.data?.identite ?? []).map((r) => (
                <div key={r.libelle} className="py-2 flex flex-wrap gap-x-4 gap-y-1">
                  <dt className="text-gray-500 dark:text-gray-400 min-w-[13rem]">{r.libelle}</dt>
                  <dd className="flex-1 min-w-[12rem]">
                    {r.valeur ? (
                      <span className="text-gray-900 dark:text-gray-100">{r.valeur}</span>
                    ) : (
                      <span className="text-amber-700 dark:text-amber-400">
                        à compléter — {r.ou}
                      </span>
                    )}
                  </dd>
                </div>
              ))}
            </dl>

            {/* Ce qui manque se dit avant le téléchargement : un registre
                incomplet qu'on imprime sans le savoir est pire qu'un registre
                qu'on n'a pas encore. */}
            {(registre.data?.manquants?.length ?? 0) > 0 && (
              <p className="text-sm text-amber-800 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl px-3 py-2">
                <strong>
                  {registre.data!.manquants.length} rubrique
                  {registre.data!.manquants.length > 1 ? 's' : ''} manque
                  {registre.data!.manquants.length > 1 ? 'nt' : ''} encore.
                </strong>{' '}
                Elles ne peuvent être remplies que par vous, dans les{' '}
                <Link to="/admin/parametres" className="underline font-medium">
                  paramètres
                </Link>
                . Le document reste téléchargeable en l'état, et signale ce qui manque.
              </p>
            )}

            <div className="flex flex-wrap gap-2">
              <a
                href={lienRegistre('pdf')}
                className="btn-primary inline-flex items-center gap-2 text-sm"
              >
                <Download className="w-4 h-4" /> Télécharger le PDF
              </a>
              <a
                href={lienRegistre('html')}
                target="_blank"
                rel="noopener"
                className="btn-outline inline-flex items-center gap-2 text-sm"
              >
                Lire dans un onglet
              </a>
            </div>
          </>
        )}
      </section>

      {/* --------------------- Clause de sous-traitance (art. 28) --------------------- */}
      <section className="card space-y-4">
        <div className="flex items-center gap-2">
          <PenLine className="w-5 h-5 text-primary-600 shrink-0" />
          <h2 className="font-semibold text-gray-900 dark:text-gray-100">
            Votre contrat de sous-traitance avec l'éditeur
          </h2>
        </div>

        <p className="text-sm text-gray-600 dark:text-gray-400">
          L'article 28 exige un contrat écrit entre vous, responsable de traitement, et l'éditeur
          qui héberge vos données. Le texte est reproduit dans votre registre ; ce qui suit permet
          de l'adopter réellement. Tant que les deux signatures ne sont pas réunies, il ne reste
          qu'un modèle dans un document et n'engage personne.
        </p>

        {clause.isPending ? (
          <Loader2 className="w-5 h-5 animate-spin text-primary-600" />
        ) : clause.isError ? (
          <p className="text-sm text-amber-800 dark:text-amber-300">
            La clause n'est pas encore disponible sur cet espace.
          </p>
        ) : (
          <>
            {/* Les deux parties côte à côte : ce qui manque doit sauter aux
                yeux, et ce n'est jamais la même moitié. */}
            <div className="grid gap-3 sm:grid-cols-2">
              {(
                [
                  ['Pour votre association', clause.data!.association],
                  ["Pour l'éditeur", clause.data!.editeur],
                ] as const
              ).map(([titre, sig]) => (
                <div
                  key={titre}
                  className={`rounded-xl border px-4 py-3 ${
                    sig
                      ? 'border-emerald-200 bg-emerald-50 dark:border-emerald-800 dark:bg-emerald-900/20'
                      : 'border-amber-200 bg-amber-50 dark:border-amber-800 dark:bg-amber-900/20'
                  }`}
                >
                  <p className="text-xs uppercase tracking-wide text-gray-500 dark:text-gray-400">
                    {titre}
                  </p>
                  {sig ? (
                    <>
                      <p className="font-medium text-gray-900 dark:text-gray-100 mt-1">{sig.nom}</p>
                      <p className="text-sm text-gray-600 dark:text-gray-400">{sig.qualite}</p>
                      <p className="text-xs text-gray-500 dark:text-gray-500 mt-1">
                        Signée le{' '}
                        {new Date(sig.signeLe).toLocaleDateString('fr-FR', {
                          timeZone: 'Europe/Paris',
                          day: '2-digit',
                          month: 'long',
                          year: 'numeric',
                        })}
                        {sig.adresseIp ? ` depuis ${sig.adresseIp}` : ''}
                      </p>
                    </>
                  ) : (
                    <p className="text-sm text-amber-800 dark:text-amber-300 mt-1">
                      Signature en attente
                    </p>
                  )}
                </div>
              ))}
            </div>

            <p className="text-xs text-gray-500 dark:text-gray-500">
              Version {clause.data!.version} du texte, empreinte{' '}
              <code className="font-mono">{clause.data!.empreinteCourte}</code>. Cette empreinte
              scelle les mots exacts qui ont été signés : si le texte change, elle change avec lui
              et les signatures cessent de couvrir la version nouvelle.
            </p>

            {clause.data!.complete ? (
              <p className="text-sm text-emerald-800 dark:text-emerald-300 bg-emerald-50 dark:bg-emerald-900/20 border border-emerald-200 dark:border-emerald-800 rounded-xl px-3 py-2">
                <strong>Clause en vigueur.</strong> Elle figure signée dans votre registre, et
                l'éditeur vous doit notification de toute violation sous 48 heures. Il s'agit d'une
                signature électronique simple au sens du règlement eIDAS : recevable en preuve, elle
                n'est ni avancée ni qualifiée.
              </p>
            ) : clause.data!.association ? (
              <p className="text-sm text-amber-800 dark:text-amber-300 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-xl px-3 py-2">
                Vous avez signé. La clause entrera en vigueur à la contresignature de l'éditeur —
                vous n'avez rien de plus à faire.
              </p>
            ) : (
              <div className="flex flex-wrap items-center gap-3">
                <button
                  type="button"
                  className="btn-primary inline-flex items-center gap-2 text-sm"
                  onClick={() =>
                    setSignature({
                      nom: clause.data!.representantPropose ?? '',
                      qualite: 'Président',
                      lu: false,
                      motDePasse: '',
                    })
                  }
                >
                  <PenLine className="w-4 h-4" /> Signer la clause
                </button>
                <a
                  href={lienRegistre('html')}
                  target="_blank"
                  rel="noopener"
                  className="text-sm underline text-gray-600 dark:text-gray-400"
                >
                  Lire le texte avant de signer
                </a>
              </div>
            )}

            {clause.data!.anterieures.length > 0 && (
              <p className="text-xs text-gray-500 dark:text-gray-500">
                Signatures données sur une version antérieure du texte, qui ne couvrent pas
                celle-ci :{' '}
                {clause.data!.anterieures
                  .map(
                    (a) =>
                      `${a.partie === 'editeur' ? 'éditeur' : 'association'} — ${a.nom}, ${dateFr(
                        a.signeLe
                      )}`
                  )
                  .join(' ; ')}
                .
              </p>
            )}
          </>
        )}
      </section>

      {/* La fenêtre de signature. Le mot de passe y figure parce qu'une
          signature doit se rattacher à une personne, pas à une session restée
          ouverte sur un ordinateur partagé. */}
      {signature !== null && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-0 sm:p-4">
          <div className="bg-white dark:bg-gray-900 w-full sm:max-w-lg sm:rounded-2xl rounded-t-2xl max-h-[90vh] overflow-y-auto">
            <div className="p-5 space-y-4">
              <div className="flex items-center gap-2">
                <PenLine className="w-5 h-5 text-primary-600" />
                <h3 className="font-semibold text-gray-900 dark:text-gray-100">
                  Signer la clause de sous-traitance
                </h3>
              </div>

              <p className="text-sm text-gray-600 dark:text-gray-400">
                Vous engagez l'association dans les termes de la clause, version{' '}
                {clause.data?.version}. Cette signature est datée, horodatée et conservée avec
                l'empreinte du texte exact ; elle figurera dans votre registre.
              </p>

              <label className="block">
                <span className="text-sm text-gray-700 dark:text-gray-300">
                  Nom du signataire
                </span>
                <input
                  className="input mt-1"
                  value={signature.nom}
                  placeholder="Prénom et nom du représentant légal"
                  onChange={(e) => setSignature({ ...signature, nom: e.target.value })}
                />
              </label>

              <label className="block">
                <span className="text-sm text-gray-700 dark:text-gray-300">Qualité</span>
                <input
                  className="input mt-1"
                  value={signature.qualite}
                  placeholder="Président, présidente, trésorier…"
                  onChange={(e) => setSignature({ ...signature, qualite: e.target.value })}
                />
              </label>

              <label className="flex items-start gap-2 text-sm text-gray-700 dark:text-gray-300">
                <input
                  type="checkbox"
                  className="mt-1"
                  checked={signature.lu}
                  onChange={(e) => setSignature({ ...signature, lu: e.target.checked })}
                />
                <span>
                  J'ai lu la clause et j'accepte ses termes au nom de l'association.{' '}
                  <a
                    href={lienRegistre('html')}
                    target="_blank"
                    rel="noopener"
                    className="underline"
                  >
                    Relire le texte
                  </a>
                </span>
              </label>

              <label className="block">
                <span className="text-sm text-gray-700 dark:text-gray-300 inline-flex items-center gap-1.5">
                  <Lock className="w-3.5 h-3.5" /> Votre mot de passe
                </span>
                <input
                  type="password"
                  className="input mt-1"
                  value={signature.motDePasse}
                  autoComplete="current-password"
                  onChange={(e) => setSignature({ ...signature, motDePasse: e.target.value })}
                />
              </label>

              <div className="flex flex-wrap gap-2 justify-end pt-1">
                <button
                  type="button"
                  className="btn-outline text-sm"
                  onClick={() => setSignature(null)}
                >
                  Annuler
                </button>
                <button
                  type="button"
                  className="btn-primary text-sm inline-flex items-center gap-2"
                  disabled={
                    signer.isPending ||
                    !signature.lu ||
                    signature.nom.trim() === '' ||
                    signature.qualite.trim() === '' ||
                    signature.motDePasse === ''
                  }
                  onClick={() => signer.mutate()}
                >
                  {signer.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
                  Signer
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ------------------------- Durées de conservation ------------------------- */}
      <section className="card space-y-4">
        <div className="flex items-center gap-2">
          <Timer className="w-5 h-5 text-primary-600 shrink-0" />
          <h2 className="font-semibold text-gray-900 dark:text-gray-100">Durées de conservation</h2>
        </div>

        <p className="text-sm text-gray-600 dark:text-gray-400">
          Le règlement ne fixe pas de durée : il demande qu'on en fixe une, et qu'on s'y tienne.
          Garder « au cas où » n'est pas une précaution — un fichier d'adhérents de 2011 que plus
          personne n'ouvre reste un fichier d'adhérents de 2011, avec les adresses, les dates de
          naissance et les numéros des parents.
        </p>

        {durees.isPending ? (
          <Loader2 className="w-5 h-5 animate-spin text-primary-600" />
        ) : (
          <>
            <div className="space-y-3">
              {(durees.data?.durees ?? []).map((d) => (
                <div key={d.cle} className="flex flex-wrap items-start gap-3">
                  <label className="flex-1 min-w-[14rem]">
                    <span className="text-sm font-medium text-gray-900 dark:text-gray-100">
                      {d.libelle}
                    </span>
                    <span className="block text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                      {d.aide}
                    </span>
                  </label>
                  <div className="flex items-center gap-2">
                    <input
                      type="number"
                      min={0}
                      max={600}
                      className="input w-24 tabular-nums"
                      value={valeur(d.cle, d.mois)}
                      onChange={(e) =>
                        setBrouillon({ ...brouillon, [d.cle]: Math.max(0, Number(e.target.value)) })
                      }
                    />
                    <span className="text-sm text-gray-500 dark:text-gray-400 whitespace-nowrap">
                      {valeur(d.cle, d.mois) === 0 ? 'désactivé' : 'mois'}
                    </span>
                  </div>
                </div>
              ))}
            </div>

            <p className="text-xs text-gray-500 dark:text-gray-400">
              <strong>Zéro désactive la purge automatique</strong> — il faut pouvoir dire « je ne
              veux pas de suppression automatique » sans saisir un nombre absurde. La purge
              n'efface jamais un montant : elle anonymise la personne et laisse la ligne comptable
              en place.
            </p>

            <button
              type="button"
              onClick={() => enregistrer.mutate()}
              disabled={Object.keys(brouillon).length === 0 || enregistrer.isPending}
              className="btn-primary inline-flex items-center gap-2 disabled:opacity-50"
            >
              {enregistrer.isPending && <Loader2 className="w-4 h-4 animate-spin" />}
              Enregistrer les durées
            </button>
          </>
        )}
      </section>

      {/* --------------------------- Effacement à la demande --------------------------- */}
      <section className="card space-y-4">
        <div className="flex items-center gap-2">
          <UserX className="w-5 h-5 text-primary-600 shrink-0" />
          <h2 className="font-semibold text-gray-900 dark:text-gray-100">
            Effacer les données d'une personne
          </h2>
        </div>

        <p className="text-sm text-gray-600 dark:text-gray-400">
          La personne disparaît, le montant reste. Sa licence de 2024 subsiste avec sa cotisation,
          rattachée à une fiche qui ne désigne plus personne : c'est la seule façon d'accorder
          l'effacement sans rendre les comptes faux. <strong>L'opération est irréversible.</strong>
        </p>

        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
          <input
            type="search"
            className="input pl-9"
            placeholder="Un nom, un prénom, une adresse de courriel…"
            value={recherche}
            onChange={(e) => {
              setRecherche(e.target.value);
              setCible(null);
            }}
          />
        </div>

        {resultats.isFetching && <Loader2 className="w-5 h-5 animate-spin text-primary-600" />}

        {(resultats.data?.resultats?.length ?? 0) > 0 && (
          <ul className="divide-y divide-gray-100 dark:divide-gray-800 text-sm">
            {resultats.data!.resultats.map((p) => (
              <li key={`${p.type}-${p.id}`} className="py-2 flex flex-wrap items-center gap-3">
                <span className="text-xs px-2 py-0.5 rounded-full bg-gray-100 dark:bg-gray-800 text-gray-600 dark:text-gray-300">
                  {p.type === 'adherent' ? 'adhérent' : 'compte'}
                </span>
                <span className="text-gray-900 dark:text-gray-100">{p.nom}</span>
                {p.precision && <span className="text-xs text-gray-500">{p.precision}</span>}
                {p.anonyme ? (
                  <span className="ml-auto text-xs text-gray-400">déjà anonymisé</span>
                ) : (
                  <button
                    type="button"
                    onClick={() => setCible(p)}
                    className="ml-auto btn-outline text-sm py-1"
                  >
                    Effacer…
                  </button>
                )}
              </li>
            ))}
          </ul>
        )}

        {recherche.trim().length >= 2 && (resultats.data?.resultats?.length ?? 0) === 0 && !resultats.isFetching && (
          <p className="text-sm text-gray-500 dark:text-gray-400">Personne de ce nom.</p>
        )}

        {/* Ce qui part et ce qui reste, avant de trancher : un écran qui
            demande « confirmez-vous ? » sans dire ce qu'il va détruire ne
            demande rien du tout. */}
        {cible && (
          <div className="rounded-xl border-2 border-amber-300 bg-amber-50 dark:bg-amber-900/20 dark:border-amber-800 p-4 space-y-3">
            <p className="font-semibold text-amber-900 dark:text-amber-200 flex items-center gap-2">
              <AlertTriangle className="w-5 h-5 shrink-0" /> Effacer {cible.nom}
            </p>

            {apercu.isPending ? (
              <Loader2 className="w-5 h-5 animate-spin text-amber-700" />
            ) : (
              <div className="grid sm:grid-cols-2 gap-4 text-sm">
                <div>
                  <p className="font-medium text-amber-900 dark:text-amber-200">Ce qui est effacé</p>
                  <ul className="mt-1 space-y-0.5 text-amber-900 dark:text-amber-200 list-disc list-inside">
                    {(apercu.data?.apercu?.efface ?? []).map((x: string) => <li key={x}>{x}</li>)}
                  </ul>
                </div>
                <div>
                  <p className="font-medium text-amber-900 dark:text-amber-200">Ce qui est conservé</p>
                  <ul className="mt-1 space-y-0.5 text-amber-900 dark:text-amber-200 list-disc list-inside">
                    {(apercu.data?.apercu?.conserve ?? []).map((x: string) => <li key={x}>{x}</li>)}
                  </ul>
                </div>
              </div>
            )}

            <input
              className="input"
              placeholder="Motif (facultatif) : courriel du 12 mars, demande verbale…"
              value={motif}
              onChange={(e) => setMotif(e.target.value)}
            />

            {/* Le mot de passe : l'opération est irréversible, elle ne doit
                pas tenir à un clic mal placé dans une liste de résultats. */}
            <input
              type="password"
              className="input"
              placeholder="Votre mot de passe"
              value={motDePasse}
              onChange={(e) => setMotDePasse(e.target.value)}
              autoComplete="current-password"
            />

            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                onClick={() => effacer.mutate()}
                disabled={motDePasse === '' || effacer.isPending}
                className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-red-600 hover:bg-red-700 text-white text-sm font-medium disabled:opacity-50"
              >
                {effacer.isPending ? <Loader2 className="w-4 h-4 animate-spin" /> : <UserX className="w-4 h-4" />}
                Effacer définitivement
              </button>
              <button
                type="button"
                onClick={() => {
                  setCible(null);
                  setMotDePasse('');
                }}
                className="btn-outline text-sm"
              >
                Annuler
              </button>
            </div>
          </div>
        )}
      </section>
    </div>
  );
}
